/**
 * AssetLocker panel — per-workshop deliverables + outputs + uploads.
 *
 * Ported from `_legacy/ws4-assetlocker.js` lines 7-103 + the panel
 * shell in `_legacy/wd.html` lines 689-701. Legacy structure was two
 * sections (Outputs + Deliverables); this React port adds a third
 * Uploads section that surfaces user-added entries from
 * `useAssetLocker` (snapshot PNGs, chat exports, file uploads).
 *
 *   - **Outputs** (gold-tinted cards) — Transcript / Analysis /
 *     Data Tables / Report / Playback / Results (questionnaire
 *     results, facilitation-gated). Captures + Upload live in the
 *     panel header. The Brief panel keeps the questionnaire survey.
 *   - **Deliverables** (6 melon-tinted cards) — Brief / Solution
 *     Design / Team Members / Telemetry Data / Download Files /
 *     Stakeholder Assessment. Each renders with a format tag
 *     ([PDF] / [CAD] / [DOC] / …) and a download glyph. Static for
 *     this phase; real file hosting lands in Phase 6.
 *   - **Uploads** (variable; per-workshop store) — chips backed by
 *     `useAssetLocker`. Click opens the `<UploadLightbox>` previewer
 *     (image / markdown / text inline, plus real documents — PDF /
 *     .docx / .xlsx / .pptx — via `doc-preview.ts`; falls back to a
 *     download link for anything else). × on hover removes the upload +
 *     sweeps the blob-store entry via `useAssetLocker.remove`.
 *
 * The legacy `.asset-access-status` footer stays — green pulse +
 * "Access: Open" beneath the body.
 *
 * Findings is intentionally gone: legacy had it as a tab inside the
 * Analysis modal, never as its own dashboard panel.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { motion } from "motion/react";
import {
  AlertCircle,
  Camera,
  ChartBar,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Cloud,
  Database,
  Clock,
  Download,
  FileText,
  HardDrive,
  Headphones,
  Image as ImageIcon,
  Lock,
  MapPin,
  MessageSquare,
  Paperclip,
  Pencil,
  PlayCircle,
  RefreshCw,
  Table2,
  UploadCloud,
  Users,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { Workshop } from "@/content/workshops";
import { isVersionHistoryEnabled } from "@/env";
import { TagInput } from "@/components/tags/TagInput";
import type { OpenModal } from "@/features/workshop/modals/useWorkshopModals";
import { useWorkshopModals } from "@/features/workshop/modals/useWorkshopModals";
import { useJumpTarget } from "@/features/search/useJumpTarget";
import { useVersionHistory } from "@/features/versioning/useVersionHistory";
import {
  useAssetLocker,
  type Upload,
  type UploadAccent,
} from "@/stores/useAssetLocker";
import { useCaptures } from "@/stores/useCaptures";
import { useQuestionnaireResponses } from "@/stores/useQuestionnaireResponses";
import { can } from "@/lib/auth/can";
import { loadBlob, saveBlob } from "@/lib/blob-store";
import {
  downloadWorkshopBlob,
  isWorkshopBlobSyncEnabled,
} from "@/lib/workshop-blob-sync";
import { mimeFromExt, extFromFilename } from "@/lib/mime-ext";
import { MIME_EXT } from "@/lib/mime-ext";
import { fileToUpload } from "./file-upload";
import {
  binaryFallbackNote,
  buildDocPreview,
  classifyDocMime,
  renderDocxInto,
  type DocPreviewPayload,
  type SheetPreview,
  type SlidePreview,
} from "./doc-preview";
import { useAuth } from "@/hooks/use-auth";
import {
  Dialog,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";

interface Props {
  workshopId: Workshop["id"];
}

/** Outputs row — clickable cards that open the matching modal. The
 *  modal slot is left null for outputs we haven't ported yet; those
 *  render but stay non-interactive until their modal lands. */
interface OutputCard {
  id: string;
  label: string;
  icon: LucideIcon;
  modal: Exclude<OpenModal, null> | null;
}

const OUTPUTS: OutputCard[] = [
  { id: "transcript",  label: "Transcript",  icon: MessageSquare, modal: "transcript" },
  { id: "analysis",    label: "Analysis",    icon: ChartBar,      modal: "analysis" },
  { id: "data-tables", label: "Data Tables", icon: Table2,        modal: "data-tables" },
  { id: "report",      label: "Report",      icon: FileText,      modal: "report" },
  { id: "playback",    label: "Playback",    icon: PlayCircle,    modal: "playback" },
  // Facilitation-side — gated at render time by `view:questionnaire-results`.
  { id: "questionnaire-results", label: "Results", icon: ClipboardCheck, modal: "questionnaire-results" },
];

/**
 * Map MIME type → short format tag rendered under each Deliverable
 * label (mirrors the legacy `[PDF]`/`[CAD]`/`[DOC]` chrome). Resolves
 * in priority order:
 *   1. `upload.format` (explicit shortcode set on seed entries)
 *   2. derived from `upload.mimeType` (snapshots, file uploads)
 *   3. `[FILE]` fallback so every chip always shows a tag
 */
function formatTag(upload: Upload): string {
  if (upload.format) return `[${upload.format.toUpperCase()}]`;
  const mt = upload.mimeType ?? "";
  if (mt.startsWith("image/")) {
    const sub = mt.slice("image/".length).toUpperCase();
    return `[${sub === "JPEG" ? "JPG" : sub}]`;
  }
  if (mt === "text/markdown") return "[MD]";
  if (mt === "text/plain") return "[TXT]";
  if (mt === "application/json") return "[JSON]";
  if (mt === "application/pdf") return "[PDF]";
  // Office documents are real preview targets now (doc-preview.ts), so
  // their chips should say what they are rather than the generic [FILE].
  if (classifyDocMime(mt)) {
    const ext = MIME_EXT[mt];
    if (ext) return `[${ext.toUpperCase()}]`;
  }
  return "[FILE]";
}

/**
 * Resolve `Upload.icon` (a lucide name string from the store) into the
 * actual icon component. The fallback `Paperclip` reads as "generic
 * upload" which is the sane default for unknown / empty names.
 */
const UPLOAD_ICONS: Record<string, LucideIcon> = {
  MessageSquare,
  FileText,
  Image: ImageIcon,
  ImageIcon,
  ChartBar,
  Camera,
  Table2,
  PlayCircle,
  HardDrive,
  Pencil,
  Users,
  Database,
  ClipboardCheck,
  Calendar,
  MapPin,
  Headphones,
};

function resolveUploadIcon(name: string): LucideIcon {
  return UPLOAD_ICONS[name] ?? Paperclip;
}

/** Per-accent color tokens for upload chips. Mirrors the legacy
 *  gold / melon / blue / green / peach palette used elsewhere. */
const ACCENT_VAR: Record<UploadAccent, { fg: string; rgb: string }> = {
  blue:  { fg: "var(--accent-true-blue)", rgb: "var(--accent-true-blue-rgb)" },
  green: { fg: "var(--accent-green)",     rgb: "var(--accent-green-rgb)" },
  gold:  { fg: "var(--accent-gold)",      rgb: "var(--accent-gold-rgb)" },
  melon: { fg: "var(--accent-melon)",     rgb: "var(--accent-melon-rgb)" },
  peach: { fg: "var(--accent-peach)",     rgb: "var(--accent-peach-rgb)" },
};

/** Stable empty-array fallback for the zustand selector so an empty
 *  workshop bucket doesn't churn a new reference each render. */
const EMPTY_UPLOADS: readonly Upload[] = Object.freeze([]);

/** Shared chrome for the panel-header trigger buttons (Captures /
 *  Questionnaire results) so the pair reads as one control group. */
const HEADER_BUTTON_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "3px 8px",
  borderRadius: 8,
  border: "1px solid hsl(var(--border))",
  background: "var(--bg-card)",
  color: "var(--text-secondary)",
  fontSize: "var(--fs-label)",
  fontWeight: 600,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  cursor: "pointer",
};

const HEADER_BADGE_STYLE: CSSProperties = {
  marginLeft: 2,
  padding: "0 5px",
  borderRadius: 6,
  background: "rgba(var(--accent-true-blue-rgb), 0.18)",
  color: "var(--accent-true-blue)",
  fontSize: "var(--fs-micro)",
  lineHeight: 1.5,
};

export function AssetLocker({ workshopId }: Props) {
  const openModal = useWorkshopModals((s) => s.open);
  const setVersionTarget = useVersionHistory((s) => s.setTarget);
  const uploads = useAssetLocker(
    (s) => s.entriesByWorkshop[workshopId] ?? (EMPTY_UPLOADS as Upload[]),
  );
  const removeUpload = useAssetLocker((s) => s.remove);
  const addUpload = useAssetLocker((s) => s.add);
  // Live count of session captures for the prominent header button —
  // Captures is a workshop output, so its home is the Asset Locker
  // (not the pre-session Brief panel).
  const captureCount = useCaptures(
    (s) => (s.entriesByWorkshop[workshopId] ?? []).length,
  );
  const auth = useAuth();
  // Questionnaire results — facilitation-side only. The gate mirrors the
  // BFF's read grants on the `questionnaires` tier (participants submit
  // but never browse each other's answers); hiding the button is UX truth,
  // the BFF remains the security boundary.
  const canViewResults = can(auth.user, "view:questionnaire-results");
  const responseCount = useQuestionnaireResponses(
    (s) => (s.entriesByWorkshop[workshopId] ?? []).length,
  );

  // File-ingest affordance — hidden input driven by the visible button
  // + the drop region. Each selected/dropped file is persisted to the
  // blob store and minted into an Upload via `fileToUpload`, then handed
  // to the store's `add()` (which mirrors to GitHub when a PAT is set;
  // IDB-only otherwise — the R1 durability story).
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragDepthRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  // Surfaced inline below the drop region when an ingest fails — most
  // likely an IndexedDB quota/permission error from the blob write.
  // Without this a failed save dropped the file silently (no feedback).
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function handleFiles(files: File[]): Promise<void> {
    if (files.length === 0) return;
    const addedBy = auth.user?.displayName ?? auth.user?.username ?? "anon";
    setUploadError(null);
    for (const file of files) {
      try {
        const upload = await fileToUpload(file, workshopId, addedBy);
        addUpload(workshopId, upload);
      } catch {
        setUploadError(
          `Couldn't add "${file.name}" — local storage may be full or unavailable.`,
        );
      }
    }
  }

  // Lightbox state — null when closed. Holds the upload + resolved
  // preview payload (object URL for blobs, text for inline preview).
  const [preview, setPreview] = useState<PreviewState | null>(null);

  // Track the previously-displayed object URL so we revoke it after
  // the next preview replaces it (avoids revoking a still-visible URL).
  const prevObjectUrlRef = useRef<string | null>(null);
  useEffect(() => {
    return () => {
      if (prevObjectUrlRef.current) {
        URL.revokeObjectURL(prevObjectUrlRef.current);
        prevObjectUrlRef.current = null;
      }
    };
  }, []);

  async function openUpload(upload: Upload): Promise<void> {
    // Revoke the previous URL before opening a new one.
    if (prevObjectUrlRef.current) {
      URL.revokeObjectURL(prevObjectUrlRef.current);
      prevObjectUrlRef.current = null;
    }

    // Blob-backed uploads: snapshot PNGs, file uploads. Seed entries
    // without a `blobKey` stay decorative (handled by `clickable`
    // gating in `UploadCard` — `openUpload` isn't invoked at all).
    if (!upload.blobKey && !upload.githubPath) return;

    // First, try the local blob cache.
    let blob: Blob | undefined = upload.blobKey
      ? await loadBlob(upload.blobKey)
      : undefined;

    // Fallback: if the local cache misses but the entry has a synced
    // `githubPath`, re-download from the active blob backend (BFF or the
    // GitHub mirror) and re-cache. This self-heals when the blob store was
    // wiped (storage-migration, manual DevTools clear, browser eviction)
    // while the meta-list survived. Without this the chip would silently
    // no-op forever.
    if (!blob && upload.githubPath && isWorkshopBlobSyncEnabled()) {
      try {
        const fetched = await downloadWorkshopBlob(upload.githubPath);
        if (fetched) {
          // Wrap with the canonical MIME so the lightbox dispatch
          // (text-vs-image-vs-binary) doesn't get tripped by a bare
          // `application/octet-stream` from the adapter.
          const inferredMime =
            upload.mimeType ??
            mimeFromExt(
              extFromFilename(upload.githubPath.split("/").pop() ?? ""),
            );
          blob = new Blob([fetched], { type: inferredMime });
          // Re-cache so the next click is instant. `blobKey` must
          // exist on the entry (the dedupe always carries it across
          // from the synced side); if it doesn't, skip the write —
          // we still got a one-time preview without the cache.
          if (upload.blobKey) {
            void saveBlob(upload.blobKey, blob);
          }
        }
      } catch {
        // Network / adapter failure → fall through; the next branch
        // bails silently rather than throwing in the click handler.
      }
    }

    if (!blob) {
      // Truly stale chip — neither cache nor remote produced bytes. Say so:
      // a silent no-op here read as "downloads are broken" in UAT round 1.
      setUploadError(
        `"${upload.label}" isn't available right now — the file hasn't been published to storage yet or couldn't be fetched.`,
      );
      return;
    }
    setUploadError(null);
    const objectUrl = URL.createObjectURL(blob);
    prevObjectUrlRef.current = objectUrl;
    const mimeType = upload.mimeType ?? blob.type ?? "application/octet-stream";

    // Images are dispatched first — they have a dedicated lightbox
    // body that doesn't need a text read.
    if (mimeType.startsWith("image/")) {
      setPreview({ upload, objectUrl, mimeType, kind: "image" });
      return;
    }

    // Real documents — PDF / .docx / .xlsx / .pptx — dispatch BEFORE
    // the text sniff so a genuine Office zip or PDF is never dumped
    // into the UTF-8 read. `buildDocPreview` verifies the actual bytes
    // (%PDF- / PK zip magic) and parses xlsx/pptx up front, so the
    // seeded markdown placeholders that masquerade under a `.pdf` /
    // `.docx` MIME fail the magic check, return null, and still reach
    // the sniff below. Oversize input and parse failures land the same
    // way — the fallback chain, never a broken pane.
    const doc = await buildDocPreview(blob, mimeType);
    if (doc) {
      setPreview({ upload, objectUrl, mimeType, ...doc });
      return;
    }

    // Content-sniff the blob before trusting the MIME. Many seeded
    // placeholders ship as small Markdown bodies with a `.pdf` /
    // `.doc` / `.xls` extension (so the chip's format tag stays
    // legible); the lightbox should still render them inline rather
    // than dumping the user on the "download to view" fallback.
    //
    // Rule: blobs under 1 MB that decode as UTF-8 without null bytes
    // are treated as text. Larger files OR binary content fall back
    // to the existing kind dispatch (image / binary).
    const SNIFF_MAX_BYTES = 1_000_000;
    if (blob.size < SNIFF_MAX_BYTES) {
      try {
        const text = await blob.text();
        if (text.length > 0 && !text.includes("\0")) {
          setPreview({
            upload,
            objectUrl,
            mimeType,
            kind: "text",
            textBody: text,
          });
          return;
        }
      } catch {
        // Decode failure → bail to binary fallback below.
      }
    }

    setPreview({
      upload,
      objectUrl,
      mimeType,
      kind: "binary",
      // Legacy .doc/.xls/.ppt get a clearer message than the generic
      // fallback — the OLE containers predate the OOXML parsers.
      note: binaryFallbackNote(mimeType),
    });
  }

  function closePreview(): void {
    setPreview(null);
    // Defer revoke to the next tick so the <img>/anchor that's
    // currently using the URL has a chance to detach cleanly.
    const url = prevObjectUrlRef.current;
    if (url) {
      prevObjectUrlRef.current = null;
      queueMicrotask(() => URL.revokeObjectURL(url));
    }
  }

  /**
   * Step through previewable uploads in the Deliverables grid. Seed
   * entries without a `blobKey` aren't valid lightbox targets, so we
   * filter them out before computing the neighbour index. Wraps at
   * either edge — ← on first lands on last, → on last lands on first.
   * Invoked by `UploadLightbox` for both ←/→ keys and the chevron
   * buttons; the lightbox itself stays a pure consumer of `preview`.
   */
  async function navigatePreview(direction: "prev" | "next"): Promise<void> {
    if (!preview) return;
    const previewable = uploads.filter((u) => u.blobKey);
    if (previewable.length < 2) return;
    const currentIndex = previewable.findIndex(
      (u) => u.id === preview.upload.id,
    );
    if (currentIndex === -1) return;
    const n = previewable.length;
    const nextIndex =
      direction === "next"
        ? (currentIndex + 1) % n
        : (currentIndex - 1 + n) % n;
    await openUpload(previewable[nextIndex]!);
  }

  // Count of previewable uploads — drives the chevron affordance in
  // the lightbox header (hidden when there's nothing to step to).
  const previewableCount = useMemo(
    () => uploads.filter((u) => u.blobKey).length,
    [uploads],
  );

  // Search jump: scroll to + preview a clicked upload hit. Peek without
  // clearing until the upload is actually in the list (hydrate can lag).
  useEffect(() => {
    const pending = useJumpTarget.getState().target;
    if (
      !pending ||
      pending.workshopId !== workshopId ||
      pending.category !== "upload"
    ) {
      return;
    }
    const upload = uploads.find((u) => u.id === pending.id);
    if (!upload) return;
    useJumpTarget.getState().clear();
    const el = document.querySelector<HTMLElement>(
      `[data-upload-id="${CSS.escape(pending.id)}"]`,
    );
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    void openUpload(upload);
  }, [workshopId, uploads]);

  // Reveal a freshly added chip. New uploads append at the bottom of
  // the Deliverables grid, below the fold once the locker is full —
  // without this, an upload looked like it went nowhere (UAT round 2).
  const uploadsEndRef = useRef<HTMLDivElement | null>(null);
  const prevUploadCount = useRef(uploads.length);
  useEffect(() => {
    if (uploads.length > prevUploadCount.current) {
      uploadsEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
    prevUploadCount.current = uploads.length;
  }, [uploads.length]);

  return (
    <div
      className="panel"
      onDragEnter={(e) => {
        e.preventDefault();
        dragDepthRef.current += 1;
        if (!isDragging) setIsDragging(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) setIsDragging(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        dragDepthRef.current = 0;
        setIsDragging(false);
        void handleFiles(Array.from(e.dataTransfer.files));
      }}
      style={
        isDragging
          ? {
              outline: "2px dashed rgba(var(--accent-true-blue-rgb), 0.7)",
              outlineOffset: -2,
              background: "rgba(var(--accent-true-blue-rgb), 0.04)",
            }
          : undefined
      }
    >
      <div className="panel-header">
        <div className="panel-title">
          <Lock className="panel-title-icon" aria-hidden />
          Asset Locker
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <button
            type="button"
            onClick={() => openModal("captures")}
            title="View session captures"
            aria-label={`View session captures${captureCount ? ` (${captureCount})` : ""}`}
            style={HEADER_BUTTON_STYLE}
          >
            <Camera aria-hidden style={{ width: 13, height: 13 }} />
            Captures
            {captureCount > 0 && (
              <span style={HEADER_BADGE_STYLE}>{captureCount}</span>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            aria-hidden
            onChange={(e) => {
              const files = e.target.files ? Array.from(e.target.files) : [];
              e.target.value = "";
              void handleFiles(files);
            }}
            style={{ display: "none" }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            title="Upload a file to Deliverables"
            aria-label="Upload file"
            style={{
              ...HEADER_BUTTON_STYLE,
              color: "var(--accent-true-blue)",
              borderColor: "rgba(var(--accent-true-blue-rgb), 0.45)",
            }}
          >
            <UploadCloud aria-hidden style={{ width: 13, height: 13 }} />
            Upload
          </button>
        </div>
      </div>

      <div className="panel-body relative">
        {isDragging && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
            style={{
              background: "rgba(var(--accent-true-blue-rgb), 0.1)",
              border: "1px dashed rgba(var(--accent-true-blue-rgb), 0.55)",
              color: "var(--accent-true-blue)",
              fontSize: "var(--fs-sub)",
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Drop to upload
          </div>
        )}
        {/* Outputs — gold-tinted cards, click → modal. Deliberately NOT
            pinned: at laptop heights (~768px) a band that also carried
            Outputs consumed the whole panel body and the chip grid
            vanished entirely — the exact "overflows on upload" UAT
            complaint the pin was meant to fix. Outputs scroll with the
            content. Drop files anywhere on this panel (or use Upload). */}
        <div className="asset-section-label">Outputs</div>
        {/* minmax(0,1fr): a bare 1fr track bottoms out at min-content, so
            one long unbreakable filename widened all three columns past
            the panel and the grid clipped off-screen. */}
        <div className="asset-grid" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
          {OUTPUTS.filter(
            (card) =>
              card.id !== "questionnaire-results" || canViewResults,
          ).map((card) => {
            const Icon = card.icon;
            const clickable = card.modal !== null;
            const isResults = card.id === "questionnaire-results";
            return (
              <button
                key={card.id}
                type="button"
                onClick={() => card.modal && openModal(card.modal)}
                disabled={!clickable}
                className="asset-card"
                style={{
                  cursor: clickable ? "pointer" : "default",
                  opacity: clickable ? 1 : 0.55,
                  textAlign: "left",
                }}
                title={
                  isResults
                    ? `View questionnaire results${responseCount ? ` (${responseCount})` : ""}`
                    : clickable
                      ? `Open ${card.label}`
                      : `${card.label} — coming soon`
                }
                aria-label={
                  isResults
                    ? `View questionnaire results${responseCount ? ` (${responseCount})` : ""}`
                    : undefined
                }
              >
                <div
                  className="asset-icon"
                  style={{
                    background: "rgba(var(--accent-gold-rgb), 0.15)",
                    color: "var(--accent-gold)",
                  }}
                >
                  <Icon aria-hidden />
                </div>
                <span className="asset-name">
                  {card.label}
                  {isResults && responseCount > 0 ? ` (${responseCount})` : ""}
                </span>
              </button>
            );
          })}
        </div>

        {/* Deliverables — store-backed chips (seed entries + user-added
            uploads). Each chip uses the per-upload accent and renders
            a derived format tag from `mimeType`. Clicking a chip with a
            `blobKey` opens the lightbox preview; seed entries without a
            payload stay static. Hover reveals × for removal. */}
        <div className="asset-pinned">
          <div className="asset-section-label">Deliverables</div>
          {uploadError && (
            <div
              role="alert"
              style={{
                marginBottom: 8,
                fontSize: "var(--fs-label)",
                color: "var(--accent-red)",
                letterSpacing: "0.3px",
              }}
            >
              {uploadError}
            </div>
          )}
        </div>

        {/* minmax(0,1fr): a bare 1fr track bottoms out at min-content, so
            one long unbreakable filename widened all three columns past
            the panel and the grid clipped off-screen. */}
        <div className="asset-grid" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
          {uploads.map((upload) => (
            <UploadCard
              key={upload.id}
              upload={upload}
              onOpen={() => void openUpload(upload)}
              onRemove={() => removeUpload(workshopId, upload.id)}
              onHistory={
                isVersionHistoryEnabled() && upload.githubPath
                  ? () => {
                      setVersionTarget({
                        path: upload.githubPath!,
                        label: upload.label,
                        workshopId,
                        kind: "upload",
                        sha: upload.githubSha,
                        mimeType: upload.mimeType,
                      });
                      openModal("version-history");
                    }
                  : undefined
              }
            />
          ))}
        </div>
        {/* Scroll anchor — a fresh upload smooth-scrolls its chip into
            view (the grid appends at the bottom). */}
        <div ref={uploadsEndRef} aria-hidden />
      </div>

      {/* Footer — legacy `_legacy/wd.html` line 700 had a static
          "Access: Open" pulse strip here. Phase 6 swaps the static
          string for live GitHub sync state: dot + label always read
          as "is the locker in sync with the durable backing right
          now". Clicks fire a manual reconciliation (mirrors the old
          SyncStatusPill click target). Disabled state preserves the
          legacy copy verbatim. */}
      <LockerSyncFooter />
      {preview && (
        <UploadLightbox
          state={preview}
          workshopId={workshopId}
          onClose={closePreview}
          onNavigate={navigatePreview}
          canNavigate={previewableCount > 1}
        />
      )}
    </div>
  );
}

/* ─────────────────────────── Upload chip ─────────────────────────── */

interface UploadCardProps {
  upload: Upload;
  onOpen: () => void;
  onRemove: () => void;
  /** Open the version-history modal for this entry. Present only when the
   *  upload has synced (carries a githubPath). RT-VERSIONING. */
  onHistory?: () => void;
}

function UploadCard({ upload, onOpen, onRemove, onHistory }: UploadCardProps) {
  const Icon = resolveUploadIcon(upload.icon);
  // Force melon for every Deliverable chip — the legacy Deliverables
  // section was uniformly melon (mc.html `.asset-card` + per-card
  // `--accent-melon` tint). The per-upload `color` field is kept in
  // the store for future use but doesn't drive the render here.
  const accent = ACCENT_VAR.melon;
  void upload.color;
  const [hover, setHover] = useState(false);
  // A chip is openable when it has a blob (lightbox). Seed entries
  // without a payload stay decorative.
  // A chip is openable when it has bytes (`blobKey` in the local
  // cache) OR a `githubPath` we can re-fetch on demand. Seed entries
  // without either stay decorative.
  const clickable = Boolean(upload.blobKey || upload.githubPath);
  const tag = formatTag(upload);

  return (
    <div
      data-upload-id={upload.id}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ position: "relative" }}
    >
      <button
        type="button"
        onClick={clickable ? onOpen : undefined}
        disabled={!clickable}
        className="asset-card"
        style={{
          position: "relative",
          alignItems: "flex-start",
          minHeight: 52,
          padding: "10px 9px",
          textAlign: "left",
          width: "100%",
          cursor: clickable ? "pointer" : "default",
          // Decorative chips stay full opacity — they're still meaningful
          // placeholders (legacy never dimmed unlinked deliverables).
        }}
        title={
          clickable
            ? `Preview ${upload.label}`
            : `${upload.label} — file not yet published`
        }
      >
        {/* minWidth 0 lets the name column shrink + ellipsize; without it
            this row's min-content is the full filename width. */}
        <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0, maxWidth: "100%" }}>
          <div
            className="asset-icon"
            style={{
              background: `rgba(${accent.rgb}, 0.15)`,
              color: accent.fg,
            }}
          >
            <Icon aria-hidden />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
            <span
              className="asset-name"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {upload.label}
            </span>
            {tag && (
              <span
                style={{
                  fontSize: "var(--fs-label)",
                  color: "var(--text-muted)",
                  letterSpacing: "0.5px",
                }}
              >
                {tag}
              </span>
            )}
          </div>
        </div>
        {/* Download glyph only when there are actually bytes to open — a
            dimmed glyph on a decorative chip read as a broken download in
            UAT round 1. */}
        {clickable && (
          <span
            aria-hidden
            style={{
              position: "absolute",
              bottom: 6,
              right: 8,
              color: accent.fg,
              display: "flex",
            }}
          >
            <Download style={{ width: 13, height: 13 }} aria-hidden />
          </span>
        )}
      </button>
      {/* Clock on hover — opens version history. Only for synced entries
          (a githubPath exists), so onHistory is undefined otherwise.
          RT-VERSIONING. */}
      {hover && onHistory && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onHistory();
          }}
          aria-label={`Version history for ${upload.label}`}
          title="Version history"
          style={{
            position: "absolute",
            top: 4,
            right: 24,
            width: 16,
            height: 16,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            color: "var(--text-muted)",
            border: 0,
            borderRadius: 4,
            cursor: "pointer",
            padding: 0,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color =
              "var(--accent-true-blue)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color =
              "var(--text-muted)";
          }}
        >
          <Clock aria-hidden style={{ width: 12, height: 12 }} />
        </button>
      )}
      {/* × on hover — removes the upload + sweeps its blob (via the
          store's `remove` which already calls `deleteBlob`). */}
      {hover && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={`Remove ${upload.label}`}
          title="Remove"
          style={{
            position: "absolute",
            top: 4,
            right: 4,
            width: 16,
            height: 16,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            color: "var(--text-muted)",
            border: 0,
            borderRadius: 4,
            cursor: "pointer",
            padding: 0,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color =
              "var(--accent-red)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color =
              "var(--text-muted)";
          }}
        >
          <X aria-hidden style={{ width: 11, height: 11 }} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}

/* ─────────────────────────── Lightbox ─────────────────────────── */

type PreviewState =
  | {
      upload: Upload;
      objectUrl: string;
      mimeType: string;
      kind: "image";
    }
  | {
      upload: Upload;
      objectUrl: string;
      mimeType: string;
      kind: "text";
      textBody: string;
    }
  // Real documents — pdf / docx / xlsx / pptx. The payload shapes come
  // straight from `doc-preview.ts` so the union can't drift from what
  // `buildDocPreview` actually returns.
  | ({
      upload: Upload;
      objectUrl: string;
      mimeType: string;
    } & DocPreviewPayload)
  | {
      upload: Upload;
      objectUrl: string;
      mimeType: string;
      kind: "binary";
      /** Optional sharper copy (e.g. legacy .doc/.xls/.ppt) shown in
       *  place of the generic "Inline preview not available". */
      note?: string;
    };

const EMPTY_TAGS: readonly string[] = Object.freeze([]);

/** Inline tag editor for one upload (CC-TAGGING). Reads live tags from the
 *  store and writes via the IDB-only `update()` so a tag edit never
 *  round-trips the GitHub-sync path. */
function UploadTagBar({ workshopId, uploadId, label }: { workshopId: string; uploadId: string; label: string }) {
  const wsId = workshopId as Workshop["id"];
  const tags = useAssetLocker(
    (s) =>
      s.entriesByWorkshop[wsId]?.find((u) => u.id === uploadId)?.tags ??
      (EMPTY_TAGS as string[]),
  );
  const update = useAssetLocker((s) => s.update);
  return (
    <div
      className="flex flex-shrink-0 items-center gap-2 px-4 py-2"
      style={{
        borderBottom: "1px solid rgba(var(--accent-true-blue-rgb), 0.15)",
        background: "var(--bg-panel)",
      }}
    >
      <span
        style={{
          fontSize: "var(--fs-micro)",
          color: "var(--text-muted)",
          letterSpacing: 1,
          textTransform: "uppercase",
          flex: "none",
        }}
      >
        Tags
      </span>
      <TagInput
        value={tags}
        onChange={(next) => update(wsId, uploadId, { tags: next })}
        workshopId={wsId}
        ariaLabel={`Tags for ${label}`}
        className="flex-1"
      />
    </div>
  );
}

interface UploadLightboxProps {
  state: PreviewState;
  /** Owning workshop — the bucket key the tag editor writes through. */
  workshopId: string;
  onClose: () => void;
  /**
   * Advance the preview to the previous / next previewable upload in
   * the parent's Deliverables list. Wrap-around + seed-skip is handled
   * by the parent; the lightbox is a pure consumer.
   */
  onNavigate: (direction: "prev" | "next") => void | Promise<void>;
  /** Hide the chevron affordances when there's nothing to step to. */
  canNavigate: boolean;
}

function UploadLightbox({
  state,
  workshopId,
  onClose,
  onNavigate,
  canNavigate,
}: UploadLightboxProps) {
  const downloadName = useMemo(
    () => guessDownloadName(state.upload, state.mimeType),
    [state.upload, state.mimeType],
  );

  // ←/→ keyboard nav — bound while the dialog is mounted. Escape is
  // left to the underlying Radix Dialog (it already closes on ESC).
  // Guard against firing while the user is in a text input/textarea
  // (none today, but the binary-fallback download link could grow).
  useEffect(() => {
    if (!canNavigate) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      void onNavigate(e.key === "ArrowLeft" ? "prev" : "next");
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onNavigate, canNavigate]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <PanelDialogContent
        className="border-0 bg-transparent p-0 shadow-none"
        style={{
          outline: "none",
          width: "min(calc(var(--os-vw) * 100 - 48px), 1200px)",
          maxWidth: "calc(var(--os-vw) * 100 - 48px)",
          maxHeight: "calc(var(--os-vh) * 100 - 96px)",
        }}
      >
        <DialogTitle className="sr-only">{state.upload.label}</DialogTitle>
        <DialogDescription className="sr-only">
          Preview of {state.upload.label}.
        </DialogDescription>
        <div
          className="flex w-full flex-col overflow-hidden rounded-xl"
          style={{
            // A definite height — not max-height — on the flex column. Under
            // max-height alone Chromium resolves the panes' percentage heights
            // against the UNCAPPED content height (the docx pane measured
            // 1283px inside a 697px body), so overflow-auto never engaged and
            // documents taller than the viewport simply couldn't be scrolled.
            // With an explicit height the `flex-1 min-h-0` body is definite,
            // panes resolve h-full against it, and their scrollbars work.
            height: "calc(var(--os-vh) * 100 - 96px)",
            background: "var(--bg-panel)",
            border: "1px solid rgba(var(--accent-true-blue-rgb), 0.45)",
            outline: "3px solid #7892A9",
            outlineOffset: 0,
            boxShadow:
              "0 0 60px rgba(var(--accent-true-blue-rgb), 0.10), 0 0 120px rgba(var(--accent-true-blue-rgb), 0.08)",
          }}
        >
          {/* Header */}
          <div
            className="flex flex-shrink-0 items-center gap-3 px-4 py-3"
            style={{
              borderBottom:
                "1px solid rgba(var(--accent-true-blue-rgb), 0.25)",
              background: "var(--bg-panel)",
            }}
          >
            {canNavigate && (
              <button
                type="button"
                onClick={() => void onNavigate("prev")}
                aria-label="Previous preview"
                title="Previous (Arrow Left)"
                className="modal-close-btn inline-flex items-center justify-center"
                style={{
                  width: 26,
                  height: 26,
                  padding: 0,
                }}
              >
                <ChevronLeft
                  aria-hidden
                  style={{ width: 14, height: 14 }}
                  strokeWidth={2.5}
                />
              </button>
            )}
            <span
              className="font-sans font-bold uppercase"
              style={{
                color: "var(--text-primary)",
                fontSize: "var(--fs-title)",
                letterSpacing: "2px",
              }}
            >
              {state.upload.label}
            </span>
            {canNavigate && (
              <button
                type="button"
                onClick={() => void onNavigate("next")}
                aria-label="Next preview"
                title="Next (Arrow Right)"
                className="modal-close-btn inline-flex items-center justify-center"
                style={{
                  width: 26,
                  height: 26,
                  padding: 0,
                }}
              >
                <ChevronRight
                  aria-hidden
                  style={{ width: 14, height: 14 }}
                  strokeWidth={2.5}
                />
              </button>
            )}
            <span
              className="font-mono uppercase"
              style={{
                color: "var(--text-muted)",
                fontSize: "var(--fs-label)",
                letterSpacing: "1px",
              }}
            >
              {state.mimeType}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <a
                href={state.objectUrl}
                download={downloadName}
                className="modal-close-btn inline-flex items-center gap-1.5"
                style={{
                  fontSize: "var(--fs-label)",
                  letterSpacing: "1.5px",
                  textDecoration: "none",
                }}
              >
                <Download
                  aria-hidden
                  style={{ width: 11, height: 11 }}
                  strokeWidth={2.5}
                />
                Download
              </a>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close preview"
                className="modal-close-btn inline-flex items-center gap-1.5"
                style={{
                  fontSize: "var(--fs-label)",
                  letterSpacing: "1.5px",
                }}
              >
                <X
                  aria-hidden
                  style={{ width: 11, height: 11 }}
                  strokeWidth={2.5}
                />
                Close
              </button>
            </div>
          </div>

          {/* Tags (CC-TAGGING) — edit the asset's free-form tags inline.
              IDB-only update so it never round-trips the GitHub-sync path. */}
          <UploadTagBar
            workshopId={workshopId}
            uploadId={state.upload.id}
            label={state.upload.label}
          />

          {/* Body — content depends on the preview kind. */}
          <div className="flex min-h-0 flex-1 overflow-hidden">
            {state.kind === "image" && (
              <div
                className="flex h-full w-full items-center justify-center overflow-auto"
                style={{ background: "var(--bg-deep)" }}
              >
                <img
                  src={state.objectUrl}
                  alt={state.upload.label}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
            )}
            {state.kind === "text" && (
              <pre
                className="console-scroll h-full w-full overflow-auto p-6"
                style={{
                  background: "var(--bg-panel-alt)",
                  color: "var(--text-secondary)",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "var(--fs-base)",
                  lineHeight: 1.7,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  margin: 0,
                }}
              >
                {state.textBody}
              </pre>
            )}
            {state.kind === "pdf" && (
              // Browser-native PDF viewer over the existing object URL —
              // no parsing on our side, scroll/zoom belong to the iframe.
              <iframe
                src={state.objectUrl}
                title={`PDF preview of ${state.upload.label}`}
                className="h-full w-full"
                style={{ border: 0, background: "var(--bg-deep)" }}
              />
            )}
            {state.kind === "docx" && (
              // Keyed by upload id so ←/→ between two docx files remounts
              // the pane (fresh container + fresh failure state).
              <DocxPane
                key={state.upload.id}
                docBlob={state.docBlob}
                label={state.upload.label}
                objectUrl={state.objectUrl}
                downloadName={downloadName}
              />
            )}
            {state.kind === "xlsx" && (
              <XlsxPane sheets={state.sheets} label={state.upload.label} />
            )}
            {state.kind === "pptx" && (
              <PptxPane slides={state.slides} label={state.upload.label} />
            )}
            {state.kind === "binary" && (
              <DownloadFallbackBody
                objectUrl={state.objectUrl}
                downloadName={downloadName}
                message={state.note}
              />
            )}
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}

/* ─────────────────────── Document panes ─────────────────────── */

/**
 * Shared "download to view" body — the binary fallback, also reused by
 * the docx pane when `renderAsync` rejects mid-preview. `message` lets
 * a caller sharpen the copy (legacy Office) without forking the layout.
 */
function DownloadFallbackBody({
  objectUrl,
  downloadName,
  message,
}: {
  objectUrl: string;
  downloadName: string;
  message?: string;
}) {
  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center gap-3 px-6"
      style={{ background: "var(--bg-panel-alt)" }}
    >
      <span
        className="font-sans uppercase"
        style={{
          color: "var(--text-muted)",
          fontSize: "var(--fs-label)",
          letterSpacing: "2px",
          textAlign: "center",
        }}
      >
        {message ?? "Inline preview not available"}
      </span>
      <a
        href={objectUrl}
        download={downloadName}
        className="modal-close-btn inline-flex items-center gap-1.5"
        style={{
          fontSize: "var(--fs-label)",
          letterSpacing: "1.5px",
          textDecoration: "none",
        }}
      >
        <Download
          aria-hidden
          style={{ width: 11, height: 11 }}
          strokeWidth={2.5}
        />
        Download to view
      </a>
    </div>
  );
}

/**
 * .docx pane — hands the blob to docx-preview's `renderAsync`, which
 * renders straight into the container (its own scroll area; the library
 * draws a white "page" on a grey wrapper, legible in both themes). A
 * parse failure swaps the pane for the download fallback — the parent
 * keys this component by upload id, so navigating to another docx
 * remounts with a fresh container + fresh failure state.
 */
function DocxPane({
  docBlob,
  label,
  objectUrl,
  downloadName,
}: {
  docBlob: Blob;
  label: string;
  objectUrl: string;
  downloadName: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let cancelled = false;
    renderDocxInto(docBlob, el).catch(() => {
      if (!cancelled) setFailed(true);
    });
    return () => {
      cancelled = true;
      // Drop the rendered document (docx-preview appends, never clears).
      el.innerHTML = "";
    };
  }, [docBlob]);
  if (failed) {
    return (
      <DownloadFallbackBody objectUrl={objectUrl} downloadName={downloadName} />
    );
  }
  return (
    <div
      ref={containerRef}
      aria-label={`Document preview of ${label}`}
      className="console-scroll h-full w-full overflow-auto"
      style={{ background: "var(--bg-panel-alt)" }}
    />
  );
}

/**
 * .xlsx pane — stacked per-sheet sections (sheet name + HTML table of
 * display strings). The extraction caps live in `doc-preview.ts`; a
 * clipped sheet carries `truncated` and gets a note under its table.
 */
function XlsxPane({
  sheets,
  label,
}: {
  sheets: SheetPreview[];
  label: string;
}) {
  return (
    <div
      className="console-scroll h-full w-full overflow-auto p-4"
      aria-label={`Spreadsheet preview of ${label}`}
      style={{ background: "var(--bg-panel-alt)" }}
    >
      {sheets.map((sheet) => (
        <section key={sheet.name} style={{ marginBottom: 20 }}>
          <div
            className="font-sans uppercase"
            style={{
              marginBottom: 6,
              color: "var(--text-muted)",
              fontSize: "var(--fs-label)",
              letterSpacing: "2px",
            }}
          >
            {sheet.name}
          </div>
          <table
            style={{
              borderCollapse: "collapse",
              fontSize: "var(--fs-base)",
              color: "var(--text-secondary)",
            }}
          >
            <tbody>
              {sheet.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td
                      key={c}
                      style={{
                        border: "1px solid hsl(var(--border))",
                        padding: "3px 8px",
                        maxWidth: 320,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {sheet.truncated && (
            <div
              style={{
                marginTop: 6,
                color: "var(--text-muted)",
                fontSize: "var(--fs-label)",
                letterSpacing: "0.5px",
              }}
            >
              Truncated preview — showing {sheet.rows.length} of{" "}
              {sheet.totalRows} rows × {sheet.rows[0]?.length ?? 0} of{" "}
              {sheet.totalCols} columns. Download for the full sheet.
            </div>
          )}
        </section>
      ))}
    </div>
  );
}

/**
 * .pptx pane — honest per-slide text outline (+ speaker notes), labelled
 * as such: we extract `a:t` runs from the slide XML, we don't attempt a
 * fidelity render of layouts/images.
 */
function PptxPane({
  slides,
  label,
}: {
  slides: SlidePreview[];
  label: string;
}) {
  return (
    <div
      className="console-scroll h-full w-full overflow-auto p-6"
      aria-label={`Slide text outline of ${label}`}
      style={{ background: "var(--bg-panel-alt)" }}
    >
      <div
        className="font-sans uppercase"
        style={{
          marginBottom: 14,
          color: "var(--text-muted)",
          fontSize: "var(--fs-label)",
          letterSpacing: "2px",
        }}
      >
        Text outline — slide layouts are not rendered
      </div>
      {slides.map((slide) => (
        <section key={slide.number} style={{ marginBottom: 18 }}>
          <div
            style={{
              marginBottom: 4,
              color: "var(--text-primary)",
              fontSize: "var(--fs-base)",
              fontWeight: 600,
              letterSpacing: "0.5px",
            }}
          >
            Slide {slide.number}
          </div>
          {slide.lines.length > 0 ? (
            slide.lines.map((line, i) => (
              <p
                key={i}
                style={{
                  margin: "0 0 4px",
                  color: "var(--text-secondary)",
                  fontSize: "var(--fs-base)",
                  lineHeight: 1.6,
                }}
              >
                {line}
              </p>
            ))
          ) : (
            <p
              style={{
                margin: "0 0 4px",
                color: "var(--text-muted)",
                fontSize: "var(--fs-base)",
                fontStyle: "italic",
              }}
            >
              (no text on this slide)
            </p>
          )}
          {slide.notes.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <div
                className="font-sans uppercase"
                style={{
                  marginBottom: 2,
                  color: "var(--text-muted)",
                  fontSize: "var(--fs-label)",
                  letterSpacing: "1.5px",
                }}
              >
                Notes
              </div>
              {slide.notes.map((line, i) => (
                <p
                  key={i}
                  style={{
                    margin: "0 0 3px",
                    color: "var(--text-muted)",
                    fontSize: "var(--fs-base)",
                    lineHeight: 1.6,
                  }}
                >
                  {line}
                </p>
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}

/** Produce a sensible filename for the Download anchor — derives the
 *  extension from MIME type so the file lands with the right suffix
 *  even though our blob keys are uuid-ish. */
function guessDownloadName(upload: Upload, mimeType: string): string {
  const baseRaw = upload.label.trim() || upload.id;
  // Trim noise that would confuse Windows / browsers in filenames.
  const safe = baseRaw.replace(/[\\/:*?"<>|]+/g, "-").slice(0, 80);
  const ext = MIME_EXT[mimeType] ?? "";
  if (!ext) return safe;
  return safe.toLowerCase().endsWith(`.${ext}`) ? safe : `${safe}.${ext}`;
}

// `MIME_EXT` lives in `src/lib/mime-ext.ts` so the AssetLocker store
// (which mints `<id>.<ext>` filenames on the GitHub upload path) and the
// lightbox download anchor below share one source of truth.

/* ─────────────────────── Locker sync footer ─────────────────────── */

/**
 * Footer pulse strip for the Asset Locker panel. Replaces the legacy
 * static "Access: Open" line with live GitHub sync state — same
 * console-status shape (left dot + uppercase label), but now the dot
 * + copy carry real meaning. Used to live in `HudHeader` as a
 * standalone 28 px icon button; Phase 6 polish moved it into the
 * AssetLocker so the indicator sits next to the data it describes.
 *
 * State map (combined across `useAssetLocker` + `useCaptures`):
 *   - `disabled` (no PAT)   → green pulse + "Access: Open"
 *                              — preserves the legacy default so the
 *                              footer never goes blank in dev / demo.
 *   - `syncing`             → spinning RefreshCw + "Syncing…",
 *                              true-blue tint. Click is a no-op while
 *                              in flight.
 *   - `synced` (≥1 store)   → green pulse + "Synced HH:MM:SS".
 *                              Click triggers a manual reconciliation.
 *   - `error` (≥1 store)    → danger AlertCircle + "Sync error" with
 *                              the first message as a title attr.
 *                              Click retries.
 *   - `idle`                → muted Cloud + "Awaiting first sync".
 */
function LockerSyncFooter() {
  const assetSyncState = useAssetLocker((s) => s.syncState);
  const assetLastSyncedAt = useAssetLocker((s) => s.lastSyncedAt);
  const assetError = useAssetLocker((s) => s.syncError);
  const capturesSyncState = useCaptures((s) => s.syncState);
  const capturesLastSyncedAt = useCaptures((s) => s.lastSyncedAt);
  const capturesError = useCaptures((s) => s.syncError);

  // Combine: errors win over syncing wins over synced wins over idle.
  // Anything else stays "disabled". Mirrors the SyncStatusPill collapse.
  type CombinedState =
    | "disabled"
    | "syncing"
    | "error"
    | "synced"
    | "idle";
  let state: CombinedState;
  if (assetSyncState === "disabled" && capturesSyncState === "disabled") {
    state = "disabled";
  } else if (assetSyncState === "error" || capturesSyncState === "error") {
    state = "error";
  } else if (assetSyncState === "syncing" || capturesSyncState === "syncing") {
    state = "syncing";
  } else if (assetSyncState === "synced" || capturesSyncState === "synced") {
    state = "synced";
  } else {
    state = "idle";
  }

  function handleClick() {
    if (state === "syncing" || state === "disabled") return;
    void Promise.all([
      useAssetLocker.getState().sync(),
      useCaptures.getState().sync(),
    ]);
  }

  // Dot + label resolve from the state. Disabled keeps the legacy
  // "Access: Open" copy + pulse so demos without a PAT stay friendly.
  let dotColor: string;
  let labelColor: string;
  let label: string;
  let LabelIcon: LucideIcon | null = null;
  let labelTitle: string | undefined;

  if (state === "disabled") {
    dotColor = "var(--accent-green)";
    labelColor = "var(--accent-green)";
    label = "Access: Open";
  } else if (state === "syncing") {
    dotColor = "var(--accent-true-blue)";
    labelColor = "var(--accent-true-blue)";
    label = "Syncing…";
    LabelIcon = RefreshCw;
  } else if (state === "error") {
    dotColor = "var(--accent-danger)";
    labelColor = "var(--accent-danger)";
    label = "Sync error";
    LabelIcon = AlertCircle;
    labelTitle = assetError ?? capturesError ?? "Sync failed";
  } else if (state === "synced") {
    const mostRecent = Math.max(
      assetLastSyncedAt ?? 0,
      capturesLastSyncedAt ?? 0,
    );
    dotColor = "var(--accent-green)";
    labelColor = "var(--accent-green)";
    label = mostRecent ? `Synced ${formatHHMMSS(mostRecent)}` : "Synced";
  } else {
    dotColor = "var(--text-muted)";
    labelColor = "var(--text-muted)";
    label = "Awaiting first sync";
    LabelIcon = Cloud;
  }

  // Render: same `.asset-access-status` chrome from `src/index.css`
  // (uppercase + letter-spacing + border-top), now wrapped in a
  // button so the whole row is the click target when sync is active.
  // Disabled state stays as a plain div — no hover, no pointer.
  const interactive = state !== "disabled" && state !== "syncing";

  const inner = (
    <>
      {state === "syncing" && LabelIcon ? (
        <motion.span
          aria-hidden
          style={{ display: "inline-flex", color: dotColor }}
          animate={{ rotate: 360 }}
          transition={{ duration: 1.1, ease: "linear", repeat: Infinity }}
        >
          <LabelIcon style={{ width: 12, height: 12 }} />
        </motion.span>
      ) : LabelIcon ? (
        <LabelIcon
          aria-hidden
          style={{ width: 12, height: 12, color: dotColor }}
        />
      ) : (
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: dotColor,
            boxShadow:
              state === "synced" || state === "disabled"
                ? "var(--glow-green)"
                : undefined,
            animation:
              state === "synced" || state === "disabled"
                ? "pulse 2s infinite"
                : undefined,
          }}
        />
      )}
      <span style={{ color: labelColor }}>{label}</span>
    </>
  );

  if (!interactive) {
    return (
      <div className="asset-access-status" title={labelTitle}>
        {inner}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={handleClick}
      className="asset-access-status"
      title={labelTitle ?? "Click to sync now"}
      style={{
        background: "transparent",
        border: 0,
        // The class already sets a `border-top`; the button-element
        // would otherwise erase it via `border: 0`. Replicate the
        // border-top so the line above the row stays visible.
        borderTop: "1px solid hsl(var(--border))",
        cursor: "pointer",
        width: "100%",
        textAlign: "left",
      }}
    >
      {inner}
    </button>
  );
}

function formatHHMMSS(ms: number): string {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}
